import React from 'react';
import moment from "moment-timezone";
import {
    faCheck,
    faExclamationTriangle,
    faRefresh,
    faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

export function formatRuntimeDate(value) {
    if (!value) {
        return null;
    }

    return moment(value).format("D.M.YYYY HH:mm");
}

export function formatRuntimeDay(value) {
    if (!value) {
        return null;
    }

    return moment(value).format("D.M.YYYY");
}

export function formatRuntimeScope(since, until) {
    if (!since && !until) {
        return null;
    }

    return `${formatRuntimeDate(since) ?? "?"} -> ${formatRuntimeDate(until) ?? "?"}`;
}

export function getRunModeLabel(t, mode, kind) {
    if (kind === 'historical_precalculation') {
        if (kind === 'historical_precalculation') {
            return React.createElement(
                'span',
                {className: 'text-blue-500'},
                t("runtime.scheduler.kind.historical_precalculation", {defaultValue: "Historický přepočet"})
            );
        }
    }

    switch (mode) {
        case "regular":
            return t("runtime.mode.regular", {defaultValue: "Pravidelný"});
        case "nightly":
            return t("runtime.mode.nightly", {defaultValue: "Noční"});
        case "monthly":
            return t("runtime.mode.monthly", {defaultValue: "Měsíční"});
        case "manual":
            return t("runtime.mode.manual", {defaultValue: "Ruční"});
        default:
            return mode;
    }
}

export function getFetchModeLabel(t, mode) {
    switch (mode) {
        case "day":
            return t("runtime.fetch.day", {defaultValue: "1 den"});
        case "two_days":
            return t("runtime.fetch.two_days", {defaultValue: "2 dny"});
        case "week":
            return t("runtime.fetch.week", {defaultValue: "Týden"});
        case "month":
            return t("runtime.fetch.month", {defaultValue: "Měsíc"});
        case "two_months":
            return t("runtime.fetch.two_months", {defaultValue: "2 měsíce"});
        case "quarter":
            return t("runtime.fetch.quarter", {defaultValue: "Čtvrtletí"});
        case "half_year":
            return t("runtime.fetch.half_year", {defaultValue: "Půl roku"});
        case "year":
            return t("runtime.fetch.year", {defaultValue: "1 rok"});
        case "custom":
            return t("runtime.fetch.custom", {defaultValue: "Vlastní rozsah"});
        default:
            return mode;
    }
}

export function getProjectPhaseLabel(t, phase) {
    switch (phase) {
        case "fetching":
            return t("runtime.project.phase.fetching", {defaultValue: "Stahuji data"});
        case "queued":
            return t("runtime.project.phase.queued", {defaultValue: "Čeká na zpracování"});
        case "pending":
        case "pairing":
        case "building":
        case "publishing":
            return t("runtime.project.phase.recalculating", {defaultValue: "Zpracovávám data"});
        default:
            return null;
    }
}

export function getIntegrationPhaseLabel(t, phase) {
    switch (phase) {
        case "webhook":
            return t("runtime.integration.phase.webhook", {defaultValue: "Změny přicházejí webhookem"});
        case "fetch_pending":
            return t("runtime.integration.phase.fetch_pending", {defaultValue: "Čeká na fetch slot"});
        case "fetching":
            return t("runtime.integration.phase.fetching", {defaultValue: "Stahuji data"});
        case "waiting_for_publish":
            return t("runtime.integration.phase.waiting_for_publish", {defaultValue: "Čeká na publikaci projektu"});
        default:
            return null;
    }
}

export function getProjectRuntimeAppearance(t, projectRuntime) {
    if (projectRuntime.status === "failed") {
        return {
            color: "rose",
            icon: faExclamationTriangle,
            label: t("runtime.project.failed", {defaultValue: "Chyba"}),
            spin: false,
        };
    }

    if (projectRuntime.status === "queued") {
        return {
            color: "emerald",
            icon: faCheck,
            label: projectRuntime.lastPublishedAt
                ? t("runtime.project.updated", {defaultValue: "Aktualizováno"})
                : null,
            spin: false,
        };
    }

    if (projectRuntime.status === "running") {
        return {
            color: "yellow",
            icon: faRefresh,
            label: getProjectPhaseLabel(t, projectRuntime.phase) ?? t("runtime.project.running", {defaultValue: "Probíhá"}),
            spin: true,
        };
    }

    return {
        color: "emerald",
        icon: faCheck,
        label: projectRuntime.lastPublishedAt
            ? t("runtime.project.updated", {defaultValue: "Aktualizováno"})
            : null,
        spin: false,
    };
}

export function getIntegrationRuntimeAppearance(t, integration) {
    if (integration.status === "failed") {
        return {
            color: "rose",
            icon: faExclamationTriangle,
            label: t("runtime.integration.failed", {defaultValue: "Chyba"}),
            spin: false,
        };
    }

    if (integration.status === "not_ready") {
        return {
            color: "yellow",
            icon: faTriangleExclamation,
            label: t("runtime.integration.not_ready", {defaultValue: "Není připravena"}),
            spin: false,
        };
    }

    if (integration.status === "running") {
        return {
            color: "yellow",
            icon: faRefresh,
            label: integration.phase === "fetching"
                ? t("runtime.integration.phase.fetching", {defaultValue: "Stahuji data"})
                : null,
            spin: true,
        };
    }

    return {
        color: "emerald",
        icon: faCheck,
        label: null,
        spin: false,
    };
}

export function getOverviewIndicator(t, overview) {
    if (!overview) {
        return {
            colorClass: "text-slate-400",
            icon: faRefresh,
            spin: true,
        };
    }

    const {projectRuntime, integrations} = overview;

    if (
        projectRuntime?.status === "failed"
        || integrations?.some((integration) => integration.status === "failed")
    ) {
        return {
            colorClass: "text-red-500",
            icon: faExclamationTriangle,
            spin: false,
        };
    }

    if (
        projectRuntime?.status === "queued"
        || projectRuntime?.status === "running"
        || integrations?.some((integration) => integration.status === "queued" || integration.status === "running")
    ) {
        return {
            colorClass: "text-cyan-600",
            icon: faRefresh,
            spin: true,
        };
    }

    if (integrations?.some((integration) => integration.status === "not_ready")) {
        return {
            colorClass: "text-amber-500",
            icon: faTriangleExclamation,
            spin: false,
        };
    }

    return {
        colorClass: "text-green-600 dark:text-green-500",
        icon: faCheck,
        spin: false,
    };
}

export function getProjectRuntimeGroup(projectWithRuntime) {
    const {projectRuntime, integrations} = projectWithRuntime;

    if (
        projectRuntime.status === "failed"
        || integrations.some((integration) => integration.status === "failed" || integration.status === "not_ready")
    ) {
        return "alert";
    }

    if (
        projectRuntime.status === "queued"
        || projectRuntime.status === "running"
        || integrations.some((integration) => integration.status === "queued" || integration.status === "running")
    ) {
        return "active";
    }

    return "healthy";
}

export function sortProjectsByRuntime(projects) {
    const groupWeight = {
        alert: 0,
        active: 1,
        healthy: 2,
    };

    return [...projects].sort((left, right) => {
        const leftGroup = getProjectRuntimeGroup(left);
        const rightGroup = getProjectRuntimeGroup(right);
        const leftWeight = groupWeight[leftGroup] ?? 99;
        const rightWeight = groupWeight[rightGroup] ?? 99;

        if (leftWeight !== rightWeight) {
            return leftWeight - rightWeight;
        }

        return left.projectData.name.localeCompare(right.projectData.name, "cs");
    });
}

export function sortIntegrationsByRuntime(integrations) {
    const statusWeight = {
        failed: 0,
        not_ready: 1,
        running: 2,
        queued: 3,
        idle: 4,
    };

    return [...integrations].sort((left, right) => {
        const leftWeight = statusWeight[left.status] ?? 99;
        const rightWeight = statusWeight[right.status] ?? 99;

        if (leftWeight !== rightWeight) {
            return leftWeight - rightWeight;
        }

        return left.integrationName.localeCompare(right.integrationName, "cs");
    });
}
